import React, { useState, useEffect, useCallback } from 'react';
import { Calculator, CheckCircle, Pencil, RefreshCw, Flag, ArrowRight, Landmark, AlertTriangle, Info } from 'lucide-react';
import { bankAPI, accountingAPI } from '../../lib/api';
import { useTenant } from '../../contexts/TenantContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

function fmt(v) {
    const n = Number(v || 0);
    return n.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const SOURCE_LABEL = {
    learned: 'Learned', receipt: 'Receipt', rule: 'Rule', regime: 'VAT regime',
    profile: 'VAT profile', accountant: 'Accountant',
};

// A single account- or VAT-proposal chip: code + source + confidence, amber when flagged.
function Proposal({ kind, value }) {
    if (!value) return null;
    const pct = value.confidence != null ? `${Math.round(value.confidence * 100)}%` : '—';
    const code = kind === 'account' ? (value.code || '—') : (value.code || '—');
    const name = kind === 'account' ? value.name : '';
    return (
        <div className={`rounded-md border p-2 ${value.flagged ? 'border-amber-400 bg-amber-50' : ''}`}>
            <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wide text-muted-foreground">
                    {kind === 'account' ? 'Account' : 'VAT'}
                </span>
                <span className="font-mono text-sm font-semibold">{code}</span>
                {name && <span className="text-sm truncate">{name}</span>}
                {value.flagged && <Flag className="h-3.5 w-3.5 text-amber-600 shrink-0" />}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
                {SOURCE_LABEL[value.source] || value.source || '—'} · {pct}
                {value.note ? ` · ${value.note}` : ''}
            </div>
        </div>
    );
}

function StatusBadge({ status }) {
    if (status === 'accepted') return <Badge variant="success">Accepted</Badge>;
    if (status === 'corrected') return <Badge variant="secondary">Corrected</Badge>;
    return <Badge variant="outline">Proposed</Badge>;
}

// Soft, dismissible consistency hints (NON-blocking). Mirrors the backend clf.check_coding_consistency
// for instant feedback while editing; saved lines use the authoritative backend hints (e.coding.consistency).
const NO_ACTIVE_VAT = new Set(['', 'MOMSFRI', 'IKKEMOMS', 'I0', 'U0']);
const NONVAT_HINTS = ['gebyr', 'rente', 'bank', 'finansiel', 'pension', 'atp', 'lønsum', 'lonsum', 'forsikring', 'mellemregning'];
const VAT_REGION = { I25: 'domestic', IEU: 'eu', IREV: 'non_eu', IVEU: 'eu', IV25: 'non_eu', U25: 'domestic', UEU: 'eu', UEXP: 'non_eu' };
function vatDir(code) {
    const c = (code || '').trim().toUpperCase();
    if (!c || c === 'MOMSFRI' || c === 'IKKEMOMS') return null;
    if (c.startsWith('U')) return 'outgoing';
    if (c.startsWith('I')) return 'incoming';
    return null;
}
function acctRegion(name) {
    const n = (name || '').toLowerCase();
    if (n.includes('3. lande') || n.includes('3.lande') || n.includes('udland')) return 'non_eu';
    if (n.includes('indenfor eu') || n.includes('inden for eu') || n.includes(' eu') || n.includes('-eu')) return 'eu';
    return null;
}
function clientConsistency(accountName, vatCode, kind) {
    const out = [];
    const code = (vatCode || '').trim();
    const nl = (accountName || '').toLowerCase();
    const vdir = vatDir(code);
    const active = !!code && !NO_ACTIVE_VAT.has(code.toUpperCase());
    const isSale = nl.includes('salg');
    const isOwner = kind === 'owner_movement';
    const isExpense = !isSale && !isOwner;
    if (vdir === 'outgoing' && isExpense) out.push({ level: 'warning', message: 'This is an outgoing (sales) VAT code on an expense line. Did you mean an incoming (purchase) code, or no VAT (leave blank)?' });
    if (vdir === 'incoming' && isSale) out.push({ level: 'warning', message: 'This is an incoming (purchase) VAT code on a sales account. Did you mean an outgoing (sales) code?' });
    const ar = acctRegion(accountName), vr = VAT_REGION[code.toUpperCase()] || null;
    if (ar && vr && ar !== vr) {
        const p = { eu: 'EU', non_eu: 'non-EU (3. lande)', domestic: 'domestic' };
        out.push({ level: 'warning', message: `Account and VAT code disagree on region — the account looks ${p[ar]} but the VAT code is ${p[vr]}.` });
    }
    if (active && (isOwner || NONVAT_HINTS.some((h) => nl.includes(h)))) out.push({ level: 'info', message: 'Financial/bank costs, pension and owner movements are usually VAT-exempt — consider leaving VAT blank.' });
    return out;
}
function Hints({ items }) {
    if (!items || !items.length) return null;
    return (
        <div className="space-y-1" data-testid="coding-hints">
            {items.map((h, i) => (
                <div key={i} className={`text-xs flex items-start gap-1.5 ${h.level === 'warning' ? 'text-amber-700' : 'text-muted-foreground'}`}>
                    {h.level === 'warning' ? <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> : <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />}
                    <span>{h.message}</span>
                </div>
            ))}
        </div>
    );
}

export default function BankCodingPage() {
    const { currentTenant } = useTenant();
    const [fyOptions, setFyOptions] = useState([]);
    const [fy, setFy] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(null);
    const [editing, setEditing] = useState(null);   // bank_line_id being corrected
    const [form, setForm] = useState({ accountCode: '', vatCode: '', kind: 'pl' });
    const [chart, setChart] = useState([]);

    useEffect(() => {
        accountingAPI.getRealChart().then((r) => setChart(r.data.accounts || [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (!currentTenant) return;
        bankAPI.getLines(currentTenant.id)
            .then((r) => {
                const years = r.data.fiscal_years || [];
                setFyOptions(years);
                setFy((prev) => prev || (years.length ? String(years[years.length - 1]) : null));
            })
            .catch(() => {});
    }, [currentTenant]);

    const load = useCallback(async () => {
        if (!currentTenant || !fy) { setData(null); return; }
        setLoading(true);
        try {
            const r = await bankAPI.getCoding(currentTenant.id, fy);
            setData(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not load coding');
        } finally { setLoading(false); }
    }, [currentTenant, fy]);

    useEffect(() => { load(); }, [load]);

    const accept = async (lineId) => {
        setBusy(lineId);
        try {
            const r = await bankAPI.acceptCoding(currentTenant.id, lineId);
            setData(r.data);
            toast.success('Coding accepted');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Accept failed');
        } finally { setBusy(null); }
    };

    const startEdit = (e) => {
        setEditing(e.bank_line.id);
        setForm({
            accountCode: e.coding.account.code || '',
            vatCode: e.coding.vat.code || '',
            kind: e.coding.kind || 'pl',
        });
    };

    const submitCorrection = async (lineId) => {
        setBusy(lineId);
        try {
            const acct = chart.find((a) => String(a.konto) === String(form.accountCode));
            const r = await bankAPI.correctCoding(currentTenant.id, lineId, {
                accountCode: form.kind === 'owner_movement' ? '' : form.accountCode,
                accountName: acct ? acct.navn : undefined,
                vatCode: form.kind === 'owner_movement' ? '' : form.vatCode,
                kind: form.kind,
            });
            setData(r.data);
            setEditing(null);
            toast.success('Coding corrected — learned for next time');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Correction failed');
        } finally { setBusy(null); }
    };

    if (!currentTenant) {
        return (
            <div className="space-y-6" data-testid="bank-coding-page">
                <h1 className="font-heading text-3xl font-bold">Bank coding</h1>
                <Card><CardContent className="py-12 text-center text-muted-foreground">Select a company to review coding.</CardContent></Card>
            </div>
        );
    }

    const s = data?.summary;
    const entries = data?.entries || [];

    return (
        <div className="space-y-8" data-testid="bank-coding-page">
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="font-heading text-3xl font-bold flex items-center gap-2">
                        <Calculator className="h-7 w-7 text-primary" /> Bank coding
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {currentTenant.name} — propose an account + VAT code for every bank line. Review and accept or correct; the contra and posting come later.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={fy || ''} onValueChange={setFy}>
                        <SelectTrigger className="h-9 w-36" data-testid="coding-fy"><SelectValue placeholder="Fiscal year" /></SelectTrigger>
                        <SelectContent>
                            {fyOptions.map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
                        </SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" onClick={load} title="Recompute" data-testid="coding-refresh">
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
            </div>

            {!fy ? (
                <Card><CardContent className="py-12 text-center text-muted-foreground">
                    No bank lines yet — import a statement on the Bank statements screen first.
                </CardContent></Card>
            ) : (
                <>
                    {s && (
                        <div className="flex flex-wrap gap-3 text-sm items-center">
                            <Badge variant="outline">Lines {s.total}</Badge>
                            <Badge variant="success">Accepted {s.accepted}</Badge>
                            <Badge variant="secondary">Corrected {s.corrected}</Badge>
                            <Badge variant="outline" className="border-amber-400 text-amber-700">Flagged {s.flagged_for_review}</Badge>
                            <span className="text-muted-foreground self-center">
                                coding accuracy {s.coding_accuracy != null ? `${Math.round(s.coding_accuracy * 100)}%` : '— (no decisions yet)'}
                            </span>
                        </div>
                    )}

                    <div className="text-xs text-muted-foreground" data-testid="vat-legend">
                        VAT codes: <span className="font-mono">I</span> = incoming/purchase, <span className="font-mono">U</span> = outgoing/sales. “No VAT” = leave the field blank.
                    </div>

                    <Card>
                        <CardHeader><CardTitle className="text-lg">Coded lines ({entries.length})</CardTitle></CardHeader>
                        <CardContent className="space-y-2">
                            {entries.length === 0 ? (
                                <p className="text-sm text-muted-foreground py-4 text-center">No matched or bank-only lines to code.</p>
                            ) : entries.map((e) => {
                                const owner = e.coding.kind === 'owner_movement';
                                return (
                                    <div key={e.bank_line.id} className="rounded-md border p-3 space-y-2" data-testid={`coding-${e.bank_line.id}`}>
                                        <div className="flex items-center gap-3">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium truncate flex items-center gap-2">
                                                    {e.bank_line.text || '—'}
                                                    <Badge variant="outline" className="shrink-0">{e.bucket === 'matched' ? 'Matched' : 'Bank-only'}</Badge>
                                                    {owner && <Badge variant="outline" className="shrink-0 border-blue-400 text-blue-700"><Landmark className="h-3 w-3 mr-1" />Owner / transfer</Badge>}
                                                </div>
                                                <div className="text-xs text-muted-foreground font-mono">{e.bank_line.date} · {fmt(e.bank_line.amount_signed)}{e.receipt ? ` · receipt: ${e.receipt.supplier || e.receipt.filename || '—'}` : ''}</div>
                                            </div>
                                            <StatusBadge status={e.coding.coding_status} />
                                        </div>

                                        {owner ? (
                                            <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md p-2">
                                                Balance-sheet / owner-movement — not a P&amp;L expense or revenue. No debit account; the entry is routed to the owner contra below.
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                <Proposal kind="account" value={e.coding.account} />
                                                <Proposal kind="vat" value={e.coding.vat} />
                                            </div>
                                        )}

                                        {/* Soft, non-blocking consistency hints for the current (account, VAT) combination */}
                                        {editing !== e.bank_line.id && <Hints items={e.coding.consistency} />}

                                        {/* P4 contra (credit side) — full entry = debit account + credit contra */}
                                        {e.contra && (
                                            <div className={`text-xs flex items-center gap-1.5 ${e.contra.flagged ? 'text-amber-700' : 'text-muted-foreground'}`} data-testid={`contra-${e.bank_line.id}`}>
                                                <span className="uppercase tracking-wide">Credit (contra):</span>
                                                <span className="font-mono font-semibold">{e.contra.code || '—'}</span>
                                                <span>{e.contra.name}</span>
                                                <span className="text-muted-foreground">({e.contra.type})</span>
                                                {e.contra.flagged && <span>· {e.contra.note}</span>}
                                            </div>
                                        )}

                                        {editing === e.bank_line.id ? (
                                            <div className="rounded-md border bg-muted/30 p-3 space-y-2" data-testid={`edit-${e.bank_line.id}`}>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs w-20 text-muted-foreground">Kind</span>
                                                    <Select value={form.kind} onValueChange={(v) => setForm((f) => ({ ...f, kind: v }))}>
                                                        <SelectTrigger className="h-8 w-56"><SelectValue /></SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="pl">P&amp;L (expense / revenue)</SelectItem>
                                                            <SelectItem value="owner_movement">Owner / own-account transfer</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                {form.kind !== 'owner_movement' && (
                                                    <>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs w-20 text-muted-foreground">Account</span>
                                                            <Select value={form.accountCode} onValueChange={(v) => setForm((f) => ({ ...f, accountCode: v }))}>
                                                                <SelectTrigger className="h-8 flex-1" data-testid={`acct-select-${e.bank_line.id}`}><SelectValue placeholder="Choose an account…" /></SelectTrigger>
                                                                <SelectContent className="max-h-72">
                                                                    {chart.map((a) => (
                                                                        <SelectItem key={a.konto} value={String(a.konto)}>{a.konto} — {a.navn}</SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs w-20 text-muted-foreground">VAT code</span>
                                                            <Input className="h-8 w-40 font-mono" value={form.vatCode}
                                                                   onChange={(ev) => setForm((f) => ({ ...f, vatCode: ev.target.value }))}
                                                                   placeholder="e.g. I25 / IEU" data-testid={`vat-input-${e.bank_line.id}`} />
                                                        </div>
                                                        {/* Live sanity check as the accountant sets the code — non-blocking */}
                                                        <Hints items={clientConsistency(
                                                            (chart.find((a) => String(a.konto) === String(form.accountCode)) || {}).navn,
                                                            form.vatCode, form.kind)} />
                                                    </>
                                                )}
                                                <div className="flex items-center gap-2 pt-1">
                                                    <Button size="sm" disabled={busy === e.bank_line.id} onClick={() => submitCorrection(e.bank_line.id)} data-testid={`save-${e.bank_line.id}`}>Save correction</Button>
                                                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-1">
                                                {e.coding.coding_status !== 'accepted' && !owner && (
                                                    <Button size="sm" variant="outline" disabled={busy === e.bank_line.id}
                                                            onClick={() => accept(e.bank_line.id)} data-testid={`accept-${e.bank_line.id}`}>
                                                        <CheckCircle className="h-4 w-4 mr-1" /> Accept
                                                    </Button>
                                                )}
                                                <Button size="sm" variant="ghost" disabled={busy === e.bank_line.id}
                                                        onClick={() => startEdit(e)} data-testid={`correct-${e.bank_line.id}`}>
                                                    <Pencil className="h-4 w-4 mr-1" /> Correct
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}
